// Installer fee payments, per Job (25 October 2026). Jacky: "payment always point to a job, invoice details are
// optional" — and mockup v2, approved: each Job a crew worked on is a row, Mark paid, Cash or Transfer. One pure
// reading of the agreed fees (crm.job_labour_fee) and the payments (crm.installer_payment), shared by the crew page,
// the Job card, the Installers list, the crew's app and EmmA's read, so every screen says the same thing.
//
// Money is EX GST throughout: an agreed fee is ex GST, a payment records its amount ex GST (and the GST, when the crew
// charges it, separately). Sums run in cents.
export const INSTALLER_PAYMENT_METHODS = ['cash', 'transfer'];
const cents = (v) => Math.round(Number(v ?? 0) * 100);
const aud = (c) => c / 100;
/** Each crew's fee on each Job: agreed, paid, still to pay. A Job with an agreed fee and no payment reads 'unpaid'. */
export function jobPayViews(agreed, payments) {
    const key = (party, job) => `${party}\u0000${job}`;
    const out = new Map();
    const at = (party, job) => {
        const k = key(party, job);
        let v = out.get(k);
        if (!v) {
            v = { party_id: party, job_ref: job, agreed: null, pays: [] };
            out.set(k, v);
        }
        return v;
    };
    for (const a of agreed) {
        const v = at(a.party_id, a.job_ref);
        v.agreed = (v.agreed ?? 0) + cents(a.fee_aud);
    }
    for (const p of payments)
        if (!p.voided_at)
            at(p.party_id, p.job_ref).pays.push(p);
    return [...out.values()].map((v) => {
        const pays = v.pays.sort((a, b) => a.paid_on.localeCompare(b.paid_on) || a.id.localeCompare(b.id));
        const paid = pays.reduce((n, p) => n + cents(p.amount_ex_gst), 0);
        const agreedC = v.agreed ?? 0;
        const status = paid <= 0 ? 'unpaid' : paid >= agreedC ? 'paid' : 'part_paid';
        const last = pays.at(-1);
        return {
            party_id: v.party_id, job_ref: v.job_ref, agreed_aud: v.agreed === null ? null : aud(v.agreed),
            paid_ex_gst: aud(paid), balance_ex_gst: aud(Math.max(0, agreedC - paid)), status,
            payments: pays.map((p) => ({ id: p.id, amount_ex_gst: aud(cents(p.amount_ex_gst)), gst_aud: aud(cents(p.gst_aud)), method: p.method, paid_on: p.paid_on, reference: p.reference ?? '', invoice_number: p.invoice_number ?? null, batch_id: p.batch_id ?? null })),
            last_payment: last ? { method: last.method, paid_on: last.paid_on } : null,
        };
    }).sort((a, b) => a.job_ref.localeCompare(b.job_ref) || a.party_id.localeCompare(b.party_id));
}
/** What is still to pay across Jobs, ex GST. */
export function stillToPay(jobs) {
    return aud(jobs.reduce((n, j) => n + cents(j.balance_ex_gst), 0));
}
/** The job ref's running number (J-1026-10307 → 10307): the tie-break, and the order when a Job has no crew-day. */
const seq = (ref) => Number(/(\d+)$/.exec(ref)?.[1] ?? Number.MAX_SAFE_INTEGER);
export function oldestFirst(a, b) {
    if (a.first_day && b.first_day && a.first_day !== b.first_day)
        return a.first_day.localeCompare(b.first_day);
    if (!!a.first_day !== !!b.first_day)
        return a.first_day ? -1 : 1;
    return seq(a.job_ref) - seq(b.job_ref) || a.job_ref.localeCompare(b.job_ref);
}
export function splitLumpSum(amount_ex_gst, jobs) {
    const owing = jobs.filter((j) => cents(j.balance_ex_gst) > 0);
    const eligible = owing.filter((j) => !j.day_ahead).sort(oldestFirst);
    const owed = eligible.reduce((n, j) => n + cents(j.balance_ex_gst), 0);
    let rest = Math.max(0, cents(amount_ex_gst));
    const lines = [];
    for (const j of eligible) {
        if (rest <= 0)
            break;
        const before = cents(j.balance_ex_gst), share = Math.min(before, rest);
        rest -= share;
        lines.push({ job_ref: j.job_ref, amount_ex_gst: aud(share), balance_before: aud(before), balance_after: aud(before - share) });
    }
    return { lines, skipped: owing.filter((j) => j.day_ahead).sort(oldestFirst).map((j) => ({ job_ref: j.job_ref, balance_ex_gst: j.balance_ex_gst })),
        owed_ex_gst: aud(owed), left_ex_gst: aud(Math.max(0, owed - cents(amount_ex_gst))), over: cents(amount_ex_gst) > owed };
}
/** Each Job's earliest crew-day, and whether a day is still booked from `today` on — the inputs of the oldest-first order. */
export function lumpSumJobs(views, days, today) {
    return views.map((v) => {
        const own = days.filter((d) => d.job_ref === v.job_ref).map((d) => d.date).sort();
        return { job_ref: v.job_ref, balance_ex_gst: v.balance_ex_gst, first_day: own[0] ?? null, day_ahead: own.some((d) => d >= today) };
    });
}
const normalCrew = (value) => String(value ?? '').trim().toLowerCase().replace(/^team\s+/, '').replace(/\s+/g, ' ');
/** Every scheduled day that belongs to this crew: an explicit installer assignee, else the day's (or the install's) team name. */
export function crewWorkDays(rows, party) {
    const out = [];
    for (const row of rows) {
        if (!row.job_ref || String(row.gate ?? '').trim().toLowerCase() === 'cancelled')
            continue;
        for (const day of Array.isArray(row.payload?.scheduleCols) ? row.payload.scheduleCols : []) {
            const date = String(day.date ?? '').trim();
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date))
                continue;
            const explicit = day.assignee?.kind === 'installer';
            const belongs = explicit ? day.assignee?.id === party.id : normalCrew(String(day.team ?? '').trim() || row.team) === normalCrew(party.name);
            if (belongs)
                out.push({ date, job_ref: row.job_ref, completed: /complete/i.test(String(row.gate ?? '')) });
        }
    }
    return out.sort((a, b) => b.date.localeCompare(a.date) || a.job_ref.localeCompare(b.job_ref));
}
export function crewStatement(input) {
    const suburb = (ref) => input.suburbs?.[ref] ?? null;
    const ahead = new Map();
    for (const d of input.days)
        if (d.date >= input.today) {
            const was = ahead.get(d.job_ref);
            if (!was || d.date < was)
                ahead.set(d.job_ref, d.date);
        }
    const fee = new Map(input.confirmed.map((c) => [c.job_ref, Number(c.total_aud)]));
    const views = jobPayViews(input.confirmed.map((c) => ({ party_id: input.party_id, job_ref: c.job_ref, fee_aud: c.total_aud })), input.payments.filter((p) => p.party_id === input.party_id));
    const owing = views.filter((v) => v.agreed_aud !== null && v.balance_ex_gst > 0 && !ahead.has(v.job_ref))
        .map((v) => ({ job_ref: v.job_ref, suburb: suburb(v.job_ref), confirmed_aud: v.agreed_aud, paid_ex_gst: v.paid_ex_gst, balance_ex_gst: v.balance_ex_gst, status: v.status }));
    const paid = views.flatMap((v) => v.payments.map((p) => ({ job_ref: v.job_ref, suburb: suburb(v.job_ref), amount_ex_gst: p.amount_ex_gst, method: p.method, paid_on: p.paid_on, invoice_number: p.invoice_number })))
        .sort((a, b) => b.paid_on.localeCompare(a.paid_on) || a.job_ref.localeCompare(b.job_ref));
    const upcoming = [...ahead.entries()].map(([job_ref, first_day]) => ({ job_ref, suburb: suburb(job_ref), first_day, fee_aud: fee.get(job_ref) ?? null }))
        .sort((a, b) => a.first_day.localeCompare(b.first_day) || a.job_ref.localeCompare(b.job_ref));
    return { still_to_pay: stillToPay(owing.map((o) => ({ balance_ex_gst: o.balance_ex_gst }))), owing, paid, upcoming };
}
//# sourceMappingURL=installer-ledger.js.map